/*eslint max-lines-per-function: ["error", 500]*/
/*eslint complexity: ["error", 110]*/
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { StatisticsSPD } from '../../../common/spdCore/autogenerated/spdApiClient';
import { SuperAdminCardMenu } from '../../components/SuperAdminPanel/SuperAdminCardMenu';
import { SuperAdminJudgmentTab } from '../../components/SuperAdminPanel/SuperAdminJudgmentTab';
import { SuperAdminExamsTab } from '../../components/SuperAdminPanel/SuperAdminExamsTab';
import { SuperAdminExamsByTypeTab } from '../../components/SuperAdminPanel/SuperAdminExamsByTypeTab';
import { SuperAdminInvoicesReceiptsTab } from '../../components/SuperAdminPanel/SuperAdminInvoicesReceiptsTab';
import { SuperAdminOverdueTab } from '../../components/SuperAdminPanel/SuperAdminOverdueTab';
import { SuperAdminPaymentsTab } from '../../components/SuperAdminPanel/SuperAdminPaymentsTab';
import { SuperAdminInvoicesByPaymentTab } from '../../components/SuperAdminPanel/SuperAdminInvoicesByPaymentTab';
import mathUtils from '../../../utils/mathUtils';
import numberFormatter from '../../../common/numberFormatter';
import { SuperAdminRegisterTab } from '../../components/SuperAdminPanel/SuperAdminRegisterTab';
import { SuperAdminTopCompaniesInvoicesTab } from '../../components/SuperAdminPanel/SuperAdminTopCompaniesInvoicesTab';
import { SuperAdminTopCompaniesJudgmentsTab } from '../../components/SuperAdminPanel/SuperAdminTopCompaniesJudgmentsTab';
import {
  DateRangeState,
  initDefaultLastDaysRange,
  toDateEnd,
  toDateStart,
} from '../../components/SuperAdminPanel/SuperAdminTopCompaniesBase';
import { SuperAdminDateRangeFilter } from '../../components/SuperAdminPanel/SuperAdminDateRangeFilter';

type SuperAdminPanelKey =
  | 'allSystem'
  | 'thisYear'
  | 'last30Days'
  | 'byRegister'
  | 'judgments'
  | 'exams'
  | 'examsByType'
  | 'invoicesReceipts'
  | 'invoicesByPayment'
  | 'payments'
  | 'overdueInvoices'
  | 'topCompaniesJudgments'
  | 'topCompaniesInvoices';

type MenuGroup = {
  title: string;
  items: Array<{ key: SuperAdminPanelKey; label: string }>;
};

export const SuperAdminPanelView: React.FC = () => {
  const spd = useSpdCore();
  const [dataStatistic, setDataStatistic] = useState<StatisticsSPD>();
  const [activeKey, setActiveKey] = useState<SuperAdminPanelKey>('allSystem');
  const [summaryFilters, setSummaryFilters] = useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [appliedSummaryFilters, setAppliedSummaryFilters] =
    useState<DateRangeState>({
      dateFrom: '',
      dateTo: '',
    });
  const productBackground = process.env.REACT_APP_BACKGROUND || '#539A61';
  const developBackground = process.env.REACT_APP_BACKGROUND || '#4d7298';
  const navAccentColor =
    process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test'
      ? developBackground
      : productBackground;

  const menuGroups: MenuGroup[] = useMemo(
    () => [
      {
        title: 'Podsumowania',
        items: [
          { key: 'allSystem', label: 'Dane ogółem' },
          { key: 'thisYear', label: 'Bieżący rok' },
          {
            key: 'last30Days',
            label: 'Zakres dat',
          },
        ],
      },
      {
        title: 'Zestawienia orzeczeń/badań',
        items: [
          { key: 'byRegister', label: 'Wg rejestrów' },
          { key: 'judgments', label: 'Orzeczenia' },
          { key: 'exams', label: 'Zakończone badania' },
          { key: 'examsByType', label: 'Rodzaje badań' },
        ],
      },
      {
        title: 'Zestawienia finansowe',
        items: [
          { key: 'invoicesReceipts', label: 'Faktury i paragony' },
          { key: 'invoicesByPayment', label: 'Faktury wg terminu płatności' },
          { key: 'payments', label: 'Wpłaty' },
        ],
      },
      {
        title: 'Klienci',
        items: [
          { key: 'overdueInvoices', label: 'Przeterminowane należności' },
          {
            key: 'topCompaniesJudgments',
            label: 'Największe firmy wg orzeczeń',
          },
          {
            key: 'topCompaniesInvoices',
            label: 'Największe firmy wg kwot',
          },
        ],
      },
    ],
    [],
  );

  const allItems = useMemo(
    () => menuGroups.flatMap(group => group.items),
    [menuGroups],
  );

  useEffect(() => {
    void initDefaultLastDaysRange(
      spd.params.getReportLastDays,
      range => {
        setSummaryFilters(range);
        setAppliedSummaryFilters(range);
      },
      error => {
        console.error('Error during report last days param fetch', error);
      },
    );
  }, [spd.params]);

  useEffect(() => {
    spd.reports
      .reportStatisticsSPDCounts(
        toDateStart(appliedSummaryFilters.dateFrom),
        toDateEnd(appliedSummaryFilters.dateTo),
      )
      .then(data => setDataStatistic(data))
      .catch(error => {
        console.error('Error during report statistic SPD', error);
      });
  }, [spd.reports, appliedSummaryFilters]);

  const summaryClassByPeriod: Record<string, string | undefined> = {
    allSystem: undefined,
    thisYear: 'stat-value-green',
    last30Days: 'stat-value-red',
  };

  const summaryDarkClassByPeriod: Record<string, string | undefined> = {
    allSystem: 'stat-value-dark-blue',
    thisYear: 'stat-value-dark-green',
    last30Days: 'stat-value-dark-red',
  };

  const renderSummaryTab = (
    period: 'allSystem' | 'thisYear' | 'last30Days',
  ) => {
    const classNameText = summaryClassByPeriod[period];
    const classNameTextDark = summaryDarkClassByPeriod[period];

    const isYear = period === 'thisYear';
    const isDateRange = period === 'last30Days';
    const periodLabel =
      period === 'allSystem'
        ? 'ogółem'
        : isYear
          ? 'bieżący rok'
          : `${appliedSummaryFilters.dateFrom} - ${appliedSummaryFilters.dateTo}`;

    const valueByPeriod = <T,>(allValue: T, yearValue: T, rangeValue: T): T =>
      period === 'allSystem' ? allValue : isYear ? yearValue : rangeValue;

    return (
      <>
        {isDateRange && (
          <SuperAdminDateRangeFilter
            filters={summaryFilters}
            setFilters={setSummaryFilters}
            onApply={() => {
              setAppliedSummaryFilters(summaryFilters);
            }}
          />
        )}
        <div className="mb-4">
          <h5 className="mb-3">Podsumowanie liczbowe - {periodLabel}</h5>
          <Row xs={1} md={3} className="g-4 mb-4">
            <SuperAdminCardMenu
              title={'Liczba zakończonych badań standardowych'}
              text={numberFormatter(
                valueByPeriod(
                  dataStatistic?.examCloseStandardCounts || 0,
                  dataStatistic?.examCloseStandardInThisYearCounts || 0,
                  dataStatistic?.examCloseStandardInLast30DaysCounts || 0,
                ),
              )}
              classNameText={classNameText}
            />
            <SuperAdminCardMenu
              title={'Liczba zakończonych badań online'}
              text={numberFormatter(
                valueByPeriod(
                  dataStatistic?.examCloseOnlineCounts || 0,
                  dataStatistic?.examCloseOnlineInThisYearCounts || 0,
                  dataStatistic?.examCloseOnlineInLast30DaysCounts || 0,
                ),
              )}
              classNameText={classNameText}
            />
            <SuperAdminCardMenu
              title={'Liczba zakończonych badań ze skierowań zewnętrznych'}
              text={numberFormatter(
                valueByPeriod(
                  dataStatistic?.examCloseExternalCounts || 0,
                  dataStatistic?.examCloseExternalInThisYearCounts || 0,
                  dataStatistic?.examCloseExternalInLast30DaysCounts || 0,
                ),
              )}
              classNameText={classNameText}
            />
          </Row>
          <Row xs={1} md={3} className="g-4 mb-4">
            <SuperAdminCardMenu
              title={'Procent zakończonych badań standardowych'}
              text={mathUtils.percent(
                valueByPeriod(
                  dataStatistic?.examCloseStandardCounts || 0,
                  dataStatistic?.examCloseStandardInThisYearCounts || 0,
                  dataStatistic?.examCloseStandardInLast30DaysCounts || 0,
                ),
                valueByPeriod(
                  dataStatistic?.examCloseCounts || 0,
                  dataStatistic?.examCloseInThisYearCounts || 0,
                  dataStatistic?.examCloseInLast30DaysCounts || 0,
                ),
              )}
              classNameText={classNameTextDark}
            />
            <SuperAdminCardMenu
              title={'Procent zakończonych badań online'}
              text={mathUtils.percent(
                valueByPeriod(
                  dataStatistic?.examCloseOnlineCounts || 0,
                  dataStatistic?.examCloseOnlineInThisYearCounts || 0,
                  dataStatistic?.examCloseOnlineInLast30DaysCounts || 0,
                ),
                valueByPeriod(
                  dataStatistic?.examCloseCounts || 0,
                  dataStatistic?.examCloseInThisYearCounts || 0,
                  dataStatistic?.examCloseInLast30DaysCounts || 0,
                ),
              )}
              classNameText={classNameTextDark}
            />
            <SuperAdminCardMenu
              title={'Procent zakończonych badań ze skierowań zewnętrznych'}
              text={mathUtils.percent(
                valueByPeriod(
                  dataStatistic?.examCloseExternalCounts || 0,
                  dataStatistic?.examCloseExternalInThisYearCounts || 0,
                  dataStatistic?.examCloseExternalInLast30DaysCounts || 0,
                ),
                valueByPeriod(
                  dataStatistic?.examCloseCounts || 0,
                  dataStatistic?.examCloseInThisYearCounts || 0,
                  dataStatistic?.examCloseInLast30DaysCounts || 0,
                ),
              )}
              classNameText={classNameTextDark}
            />
          </Row>
          <Row xs={1} md={period === 'allSystem' ? 3 : 2} className="g-4 mb-4">
            <SuperAdminCardMenu
              title={'Liczba zakończonych badań'}
              text={numberFormatter(
                valueByPeriod(
                  dataStatistic?.examCloseCounts || 0,
                  dataStatistic?.examCloseInThisYearCounts || 0,
                  dataStatistic?.examCloseInLast30DaysCounts || 0,
                ),
              )}
              classNameText={classNameText}
            />
            <SuperAdminCardMenu
              title={'Liczba orzeczeń wydanych'}
              text={numberFormatter(
                valueByPeriod(
                  dataStatistic?.judgmentsCounts || 0,
                  dataStatistic?.judgmentsInThisYearCounts || 0,
                  dataStatistic?.judgmentsInLast30DaysCounts || 0,
                ),
              )}
              classNameText={classNameText}
            />
            {period === 'allSystem' && (
              <SuperAdminCardMenu
                title={'Liczba oczekujących badań'}
                text={numberFormatter(dataStatistic?.examOpenCounts || 0)}
              />
            )}
          </Row>
        </div>

        <div>
          <h5 className="mb-3">Podsumowanie kwotowe - {periodLabel}</h5>
          <Row xs={1} md={2} className="g-4 mb-4">
            <SuperAdminCardMenu
              title={'Kwota opłaconych badań poprzez portal badanego (PLN)'}
              text={numberFormatter(
                valueByPeriod(
                  dataStatistic?.sumFinishedPayments || 0,
                  dataStatistic?.sumFinishedPaymentsInThisYear || 0,
                  dataStatistic?.sumFinishedPaymentsInLast30Days || 0,
                ),
              )}
              classNameText={classNameTextDark}
            />
            <SuperAdminCardMenu
              title={'Kwota zwrócona (PLN)'}
              text={numberFormatter(
                valueByPeriod(
                  dataStatistic?.sumReturnedPayments || 0,
                  dataStatistic?.sumReturnedPaymentsInThisYear || 0,
                  dataStatistic?.sumReturnedPaymentsInLast30Days || 0,
                ),
              )}
              classNameText={classNameTextDark}
            />
          </Row>
          <Row xs={1} md={3} className="g-4 mb-4">
            <SuperAdminCardMenu
              title={'Kwota wystawionych faktur (PLN)'}
              text={numberFormatter(
                valueByPeriod(
                  dataStatistic?.sumIssuedInvoices || 0,
                  dataStatistic?.sumIssuedInvoicesInThisYear || 0,
                  dataStatistic?.sumIssuedInvoicesInLast30Days || 0,
                ),
              )}
              classNameText={classNameTextDark}
            />
            <SuperAdminCardMenu
              title={'Kwota opłaconych faktur (PLN)'}
              text={numberFormatter(
                valueByPeriod(
                  dataStatistic?.sumPaidInvoices || 0,
                  dataStatistic?.sumPaidInvoicesInThisYear || 0,
                  dataStatistic?.sumPaidInvoicesInLast30Days || 0,
                ),
              )}
              classNameText={classNameTextDark}
            />
            <SuperAdminCardMenu
              title={'Kwota wystawionych paragonów (PLN)'}
              text={numberFormatter(
                valueByPeriod(
                  dataStatistic?.sumIssuedReceipts || 0,
                  dataStatistic?.sumIssuedReceiptsInThisYear || 0,
                  dataStatistic?.sumIssuedReceiptsInLast30Days || 0,
                ),
              )}
              classNameText={classNameTextDark}
            />
          </Row>
        </div>
      </>
    );
  };

  const renderContent = () => {
    switch (activeKey) {
      case 'allSystem':
        return renderSummaryTab('allSystem');
      case 'thisYear':
        return renderSummaryTab('thisYear');
      case 'last30Days':
        return renderSummaryTab('last30Days');
      case 'byRegister':
        return <SuperAdminRegisterTab />;
      case 'judgments':
        return <SuperAdminJudgmentTab />;
      case 'exams':
        return <SuperAdminExamsTab />;
      case 'examsByType':
        return <SuperAdminExamsByTypeTab />;
      case 'invoicesReceipts':
        return <SuperAdminInvoicesReceiptsTab />;
      case 'invoicesByPayment':
        return <SuperAdminInvoicesByPaymentTab />;
      case 'payments':
        return <SuperAdminPaymentsTab />;
      case 'overdueInvoices':
        return <SuperAdminOverdueTab />;
      case 'topCompaniesJudgments':
        return <SuperAdminTopCompaniesJudgmentsTab />;
      case 'topCompaniesInvoices':
        return <SuperAdminTopCompaniesInvoicesTab />;
      default:
        return renderSummaryTab('allSystem');
    }
  };

  return (
    <Card
      style={{
        ['--superadmin-accent' as string]: navAccentColor,
      }}
    >
      <Card.Body className="p-0">
        <Row className="g-0">
          <Col lg={3} className="superadmin-side-menu d-none d-lg-block">
            <div className="p-3">
              {menuGroups.map(group => (
                <div key={group.title} className="mb-4">
                  <div className="superadmin-side-menu-group-title mb-2">
                    {group.title}
                  </div>
                  <div className="d-grid gap-2">
                    {group.items.map(item => (
                      <Button
                        key={item.key}
                        variant="light"
                        className={`text-start superadmin-side-tab ${
                          activeKey === item.key
                            ? 'superadmin-side-tab-active'
                            : ''
                        }`}
                        onClick={() => setActiveKey(item.key)}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Col>

          <Col lg={9} className="p-3 superadmin-report-pane">
            <div className="d-lg-none mb-3">
              <Form.Select
                value={activeKey}
                onChange={e =>
                  setActiveKey(e.target.value as SuperAdminPanelKey)
                }
              >
                {allItems.map(item => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </div>
            {renderContent()}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};
